import React, { useState, useEffect, useMemo } from 'react';
import { Container, Spinner, Form } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useEntidadeContext } from '../EntidadeContext';
import { useAuthContext } from '../context/AuthContext';
import CommunicationModal from '../components/consultorio/CommunicationModal';
import Select from 'react-select';
import { useAlertModal } from '../AlertModalContext';

/* ── ícones SVG (mesmo padrão do ComunicadosConfigTab) ── */
const ICONS = {
  mail:       'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  users:      'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4',
  gift:       'M20 12v10H4V12M2 7h20v5H2V7zm10-5C9 2 8 5 8 5s3 1 4 0 1-3 0-3zm0 0c3 0 4 3 4 3s-3 1-4 0-1-3-0-3z',
  cake:       'M12 2v4M8 2v2M16 2v2M4 21v-7a2 2 0 012-2h12a2 2 0 012 2v7M4 21h16M4 15h16M12 12v-2',
  moon:       'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  tag:        'M7 7h.01M7 3h5.586a1 1 0 01.707.293l7.414 7.414a1 1 0 010 1.414l-7.586 7.586a1 1 0 01-1.414 0L4.293 12.293A1 1 0 014 11.586V6a3 3 0 013-3z',
  send:       'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  filter:     'M3 4h18M6 8h12M10 12h4',
  alert:      'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
};

/* ── helpers visuais ── */
const chipStyle = (active, accent = '#2563eb') => ({
  display: 'inline-flex', alignItems: 'center', gap: '7px',
  padding: '9px 16px', borderRadius: '999px',
  border: active ? `1.5px solid ${accent}` : '1.5px solid #e5e7eb',
  background: active ? `linear-gradient(135deg, ${accent}15, ${accent}0a)` : '#fff',
  color: active ? accent : '#6b7280',
  fontSize: `calc(12.5px * var(--font-scale))`, fontWeight: '600',
  cursor: 'pointer', transition: 'all .15s', userSelect: 'none',
});

const StatCard = ({ icon, label, value, accent }) => (
  <div style={{
    flex: '1 1 160px', backgroundColor: '#fff', borderRadius: '12px',
    border: '1px solid #e5e7eb', padding: '16px 18px',
    display: 'flex', alignItems: 'center', gap: '12px',
  }}>
    <div style={{
      width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
      background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <svg width="18" height="18" fill="none" stroke="#fff" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
    </div>
    <div>
      <div style={{ fontSize: `calc(19px * var(--font-scale))`, fontWeight: '800', color: '#1e3a5f', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: `calc(11.5px * var(--font-scale))`, color: '#6b7280', marginTop: '3px' }}>{label}</div>
    </div>
  </div>
);

const ComunicadosPage = () => {
  const { currentEntidade, isLoadingEntidade } = useEntidadeContext();
  const { loggedInUser: currentUser } = useAuthContext();
  const { showAlertModal } = useAlertModal();
  const navigate = useNavigate();

  const [pacientesList, setPacientesList] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [dynamicFilter, setDynamicFilter] = useState('');

  useEffect(() => {
    const fetchData = async (url) => {
      if (!currentEntidade) return;

      setIsLoadingData(true);
      try {
        const [pacientesResponse, tagsResponse] = await Promise.all([
          axios.get(url),
          axios.get(`/api/interesses`)
        ]);

        setPacientesList(pacientesResponse.data.pacientes || []);

        if (allTags.length === 0) {
          const formattedTags = (tagsResponse.data.interesses || []).map(tag => ({
            value: tag.id,
            label: tag.nome
          }));
          setAllTags(formattedTags);
        }

      } catch (error) {
        console.error("Erro ao buscar a lista de pacientes:", error);
        setPacientesList([]);
        setAllTags([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    if (!currentEntidade) return;
    let fetchUrl;
    if (dynamicFilter === 'pacientes_inativos') {
      fetchUrl = `/api/entidades/${currentEntidade.id}/pacientes/inativos`;
    } else {
      fetchUrl = `/api/entidades/${currentEntidade.id}/pacientes`;
    }

    fetchData(fetchUrl);

  }, [currentEntidade, dynamicFilter]);

  const filteredPacientesList = useMemo(() => {
    let tempList = [...pacientesList];

    if (dynamicFilter === 'aniversariantes_mes') {
      const currentMonth = new Date().getMonth() + 1;
      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;
        const birthMonth = new Date(paciente.data_nascimento).getMonth() + 1;
        return birthMonth === currentMonth;
      });
    } else if (dynamicFilter === 'aniversariantes_30_dias') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;

        const birthDate = new Date(paciente.data_nascimento);
        const birthMonth = birthDate.getMonth();
        const birthDay = birthDate.getDate();

        const birthdayThisYear = new Date(today.getFullYear(), birthMonth, birthDay);
        if (birthdayThisYear >= today && birthdayThisYear <= thirtyDaysFromNow) {
          return true;
        }

        const birthdayNextYear = new Date(today.getFullYear() + 1, birthMonth, birthDay);
        return birthdayNextYear >= today && birthdayNextYear <= thirtyDaysFromNow;
      });
    } else if (dynamicFilter === 'aniversariantes_dia') {
      const today = new Date();
      const currentMonth = today.getMonth() + 1;
      const currentDay = today.getDate();
      tempList = tempList.filter(paciente => {
        if (!paciente.data_nascimento) return false;
        const birthDate = new Date(paciente.data_nascimento);
        const birthMonth = birthDate.getMonth() + 1;
        const birthDay = birthDate.getDate();
        return birthMonth === currentMonth && birthDay === currentDay;
      });
    }

    if (selectedTags.length > 0) {
      const selectedTagIds = new Set(selectedTags.map(tag => tag.value));
      tempList = tempList.filter(paciente =>
        paciente.interesses && paciente.interesses.some(interesse => selectedTagIds.has(interesse.id))
      );
    }
    return tempList;
  }, [pacientesList, selectedTags, dynamicFilter]);

  const handleOpenCommunicationModal = () => {
    if (currentEntidade && currentEntidade.email_config_complete) {
      setShowModal(true);
    } else {
      showAlertModal(
        'Configuração de E-mail Pendente',
        'As configurações de envio de e-mail (SMTP) não foram realizadas. Isso é necessário para enviar comunicados. Clique em OK para configurar agora.',
        'warning',
        () => navigate('/configuracoes')
      );
    }
  };

  const hasFilters = selectedTags.length > 0 || !!dynamicFilter;

  const filterChips = [
    { key: 'aniversariantes_mes', label: 'Aniversariantes do Mês', icon: ICONS.gift, accent: '#d97706' },
    { key: 'aniversariantes_dia', label: 'Aniversariantes do Dia', icon: ICONS.cake, accent: '#db2777' },
    { key: 'pacientes_inativos', label: 'Pacientes Inativos (6 meses)', icon: ICONS.moon, accent: '#6b7280' },
  ];

  if (isLoadingEntidade || isLoadingData) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ height: '80vh', gap: '12px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '32px', height: '32px' }} />
        <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando pacientes...</span>
      </Container>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", padding: '20px' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .comunicados-select .select__control { border-radius: 10px; border-color: #e5e7eb; min-height: 42px; }
        .comunicados-select .select__control--is-focused { border-color: #2563eb !important; box-shadow: 0 0 0 3px rgba(37,99,235,.1) !important; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{
        background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px',
        padding: '22px 26px', marginBottom: '20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '46px', height: '46px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="23" height="23" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={ICONS.mail} />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: `calc(17px * var(--font-scale))`, fontWeight: '800', color: '#fff' }}>Envio de Comunicados</div>
            <div style={{ fontSize: `calc(12.5px * var(--font-scale))`, color: 'rgba(255,255,255,.75)' }}>
              Envie mensagens em massa para seus pacientes por e-mail ou WhatsApp
            </div>
          </div>
        </div>

        <button
          onClick={handleOpenCommunicationModal}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '11px 22px', borderRadius: '10px', border: 'none',
            background: '#fff', color: '#1e3a5f',
            fontSize: `calc(13.5px * var(--font-scale))`, fontWeight: '700',
            fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,.15)',
            transition: 'transform .15s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <svg width="15" height="15" fill="none" stroke="#1e3a5f" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.send} />
          </svg>
          Criar Novo Comunicado
        </button>
      </div>

      {/* ── STATS ── */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <StatCard icon={ICONS.users} label="Pacientes Cadastrados" value={pacientesList.length} accent="#2563eb" />
        <StatCard icon={ICONS.filter} label="Correspondem aos Filtros" value={filteredPacientesList.length} accent="#16a34a" />
        <StatCard icon={ICONS.tag} label="Interesses Selecionados" value={selectedTags.length} accent="#d97706" />
      </div>

      {/* ── CARD PRINCIPAL ── */}
      <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px',
          paddingBottom: '14px', borderBottom: '2px solid #f1f5f9',
        }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '7px',
            background: 'linear-gradient(135deg,#1e3a5f,#2563eb)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.filter} />
            </svg>
          </div>
          <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#1e3a5f' }}>
            Segmentação de Público
          </span>
        </div>

        {/* Filtros dinâmicos como chips */}
        <Form.Group style={{ marginBottom: '22px' }}>
          <label style={{
            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '10px',
          }}>
            Filtros Dinâmicos
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {filterChips.map(chip => {
              const active = dynamicFilter === chip.key;
              return (
                <div
                  key={chip.key}
                  style={chipStyle(active, chip.accent)}
                  onClick={() => setDynamicFilter(prev => prev === chip.key ? '' : chip.key)}
                >
                  <svg width="14" height="14" fill="none" stroke={active ? chip.accent : '#9ca3af'} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={chip.icon} />
                  </svg>
                  {chip.label}
                </div>
              );
            })}
          </div>
        </Form.Group>

        {/* Filtro por interesses */}
        <Form.Group controlId="filtroInteresses" style={{ marginBottom: '22px' }}>
          <label style={{
            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280',
            textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '10px',
          }}>
            Filtrar por Interesse
          </label>
          <Select
            isMulti
            options={allTags}
            className="comunicados-select basic-multi-select"
            classNamePrefix="select"
            placeholder="Selecione um ou mais interesses..."
            noOptionsMessage={() => 'Nenhuma tag encontrada'}
            value={selectedTags}
            onChange={setSelectedTags}
          />
        </Form.Group>

        {/* Resumo */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '12px',
          backgroundColor: hasFilters ? '#f0fdf4' : '#eff6ff',
          border: `1px solid ${hasFilters ? '#bbf7d0' : '#bfdbfe'}`,
          borderRadius: '12px', padding: '14px 18px', marginBottom: '20px',
        }}>
          <svg width="18" height="18" fill="none" stroke={hasFilters ? '#16a34a' : '#2563eb'} viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '1px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.alert} />
          </svg>
          <div style={{ fontSize: `calc(12.5px * var(--font-scale))`, color: hasFilters ? '#15803d' : '#3b82f6', lineHeight: '1.6' }}>
            {hasFilters
              ? <>Será enviado um comunicado para <strong>{filteredPacientesList.length} paciente(s)</strong> que correspondem aos filtros selecionados.</>
              : <>Utilize esta ferramenta para enviar comunicados, promoções ou avisos gerais para todos os <strong>{pacientesList.length} pacientes</strong> cadastrados.</>
            }
          </div>
        </div>

        <button
          onClick={handleOpenCommunicationModal}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '11px 22px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: '#fff',
            fontSize: `calc(13.5px * var(--font-scale))`, fontWeight: '700',
            fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          <svg width="15" height="15" fill="none" stroke="#fff" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.send} />
          </svg>
          Criar Novo Comunicado
        </button>
      </div>

      <CommunicationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        mode="comunicado"
        appointments={[]}
        pacientesList={filteredPacientesList}
        todosMedicos={[]}
        especialidades={[]}
        currentEntidade={currentEntidade}
        currentUser={currentUser}
        onSendComplete={() => {}}
      />
    </div>
  );
};

export default ComunicadosPage;
